import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";
import { useQuery, useAction } from "convex/react";
import { usePrivy } from "@privy-io/react-auth";
import { api } from "../../convex/_generated/api";
import { fetchPlaceDisplayDetails } from "../../utils/googlePlaces";
import PlacePhotoCarousel from "./PlacePhotoCarousel";
import CopyButton from "../MapScreen/HousePick/CopyButton";
import { SourceAsterisk } from "./SourceLegend";

const DAY_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DAY_ABBREV = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

const normalizeSpecialValue = (value) => {
  if (!value || value === "N/A") return "";
  return value;
};

const hasHappyHourData = (special) =>
  (special.whenHH && special.whenHH !== "N/A") ||
  (special.description && special.description !== "N/A");

function FieldLabel({ label, source, style }) {
  const sources = Array.isArray(source) ? source : [source];

  return (
    <View style={styles.fieldLabelRow}>
      <Text style={[styles.placeIdLabel, style]}>{label}</Text>
      {sources.filter(Boolean).map((src) => (
        <SourceAsterisk key={src} source={src} />
      ))}
    </View>
  );
}

function IdRow({ label, value, source, labelStyle }) {
  if (!value) return null;

  return (
    <View style={styles.idBlock}>
      <FieldLabel label={label} source={source} style={labelStyle} />
      <View style={styles.idRow}>
        <Text style={styles.placeIdHint} selectable>
          {value}
        </Text>
        <CopyButton textToCopy={value} style={styles.copyButton} />
      </View>
    </View>
  );
}

function HappyHourSection({ databaseId }) {
  const { getAccessToken } = usePrivy();
  const dailySpecials = useQuery(
    databaseId ? api.getDailySpecials.getDailySpecialsByRestaurant : "skip",
    databaseId ? { restaurantId: databaseId } : "skip"
  );
  const upsertDailySpecial = useAction(api.dailySpecialsAction.upsertDailySpecial);

  const [editingDay, setEditingDay] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newDay, setNewDay] = useState(null);
  const [draftWhenHH, setDraftWhenHH] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setEditingDay(null);
    setIsAdding(false);
    setNewDay(null);
    setDraftWhenHH("");
    setDraftDescription("");
    setSaveError(null);
  }, [databaseId]);

  const resetEditor = () => {
    setEditingDay(null);
    setIsAdding(false);
    setNewDay(null);
    setDraftWhenHH("");
    setDraftDescription("");
    setSaveError(null);
  };

  const startEditing = (special) => {
    setIsAdding(false);
    setNewDay(null);
    setEditingDay(special.dayOfWeek);
    setDraftWhenHH(normalizeSpecialValue(special.whenHH));
    setDraftDescription(normalizeSpecialValue(special.description));
    setSaveError(null);
  };

  const startAdding = (availableDays) => {
    if (!availableDays.length) return;
    setEditingDay(null);
    setIsAdding(true);
    setNewDay(availableDays[0]);
    setDraftWhenHH("");
    setDraftDescription("");
    setSaveError(null);
  };

  const saveDay = async () => {
    const dayOfWeek = isAdding ? newDay : editingDay;
    if (!databaseId || !dayOfWeek || saving) return;

    if (!draftWhenHH.trim() && !draftDescription.trim()) {
      setSaveError("Enter a time or special description");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication required. Please sign in and try again.");
      }

      await upsertDailySpecial({
        restaurantId: databaseId,
        dayOfWeek,
        whenHH: draftWhenHH.trim() || "N/A",
        description: draftDescription.trim() || "N/A",
        accessToken,
      });
      resetEditor();
    } catch (err) {
      setSaveError(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!databaseId) return null;

  if (dailySpecials === undefined) {
    return (
      <View style={styles.hhSection}>
        <FieldLabel label="Happy Hour" source="disgo" />
        <ActivityIndicator size="small" color="#4e2916" />
      </View>
    );
  }

  const existingSpecials = dailySpecials.filter(hasHappyHourData);
  const existingDays = new Set(existingSpecials.map((s) => s.dayOfWeek));
  const availableDays = DAY_ORDER.filter((day) => !existingDays.has(day));
  const isBusy = !!editingDay || isAdding;

  const renderEditor = ({ dayLabel, dayPicker }) => (
    <View style={[styles.hhEditBlock, styles.hhRowEven]}>
      <View style={styles.hhRow}>
        <Text style={[styles.hhCellDay, styles.hhDayText, styles.hhDayAlignInput]}>
          {dayPicker ? (newDay ? DAY_ABBREV[newDay] : "—") : dayLabel}
        </Text>
        <TextInput
          style={[styles.hhInput, styles.hhCellTime]}
          value={draftWhenHH}
          onChangeText={setDraftWhenHH}
          placeholder="e.g. 3-6pm"
          placeholderTextColor="#B5A59A"
          editable={!saving}
        />
        <TextInput
          style={[styles.hhInput, styles.hhCellDesc]}
          value={draftDescription}
          onChangeText={setDraftDescription}
          placeholder="Special details"
          placeholderTextColor="#B5A59A"
          multiline
          editable={!saving}
        />
      </View>
      {dayPicker ? (
        <View style={styles.dayPickerRow}>
          {availableDays.map((day) => (
            <TouchableOpacity
              key={day}
              style={[styles.dayChip, newDay === day && styles.dayChipActive]}
              onPress={() => setNewDay(day)}
              disabled={saving}
              activeOpacity={0.8}
            >
              <Text style={[styles.dayChipText, newDay === day && styles.dayChipTextActive]}>
                {DAY_ABBREV[day]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
      <View style={styles.hhEditActions}>
        <TouchableOpacity
          style={styles.cancelButton}
          onPress={resetEditor}
          disabled={saving}
          activeOpacity={0.8}
        >
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.saveButton, saving && styles.saveButtonDisabled]}
          onPress={saveDay}
          disabled={saving || (isAdding && !newDay)}
          activeOpacity={0.8}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#ffffff" />
          ) : (
            <Text style={styles.saveButtonText}>Save</Text>
          )}
        </TouchableOpacity>
      </View>
      {saveError ? <Text style={styles.saveError}>{saveError}</Text> : null}
    </View>
  );

  return (
    <View style={styles.hhSection}>
      <FieldLabel label="Happy Hour" source="disgo" />

      {existingSpecials.length === 0 && !isAdding ? (
        <Text style={styles.hhEmpty}>No happy hour data yet</Text>
      ) : (
        <View style={styles.hhTable}>
          <View style={[styles.hhRow, styles.hhHeaderRow]}>
            <Text style={[styles.hhCellDay, styles.hhHeaderText]}>Day</Text>
            <Text style={[styles.hhCellTime, styles.hhHeaderText]}>Time</Text>
            <Text style={[styles.hhCellDesc, styles.hhHeaderText]}>Special</Text>
            <View style={styles.hhCellActions} />
          </View>

          {existingSpecials.map((special, index) => {
            if (editingDay === special.dayOfWeek) {
              return (
                <View key={special.dayOfWeek}>
                  {renderEditor({
                    dayLabel: DAY_ABBREV[special.dayOfWeek] || special.dayOfWeek,
                  })}
                </View>
              );
            }

            return (
              <View
                key={special.dayOfWeek}
                style={[
                  styles.hhRow,
                  styles.hhRowCentered,
                  index % 2 === 0 ? styles.hhRowEven : styles.hhRowOdd,
                ]}
              >
                <Text style={[styles.hhCellDay, styles.hhDayText]}>
                  {DAY_ABBREV[special.dayOfWeek] || special.dayOfWeek}
                </Text>
                <Text style={styles.hhCellTimeText} numberOfLines={2}>
                  {special.whenHH}
                </Text>
                <Text style={styles.hhCellDescText} numberOfLines={3}>
                  {special.description}
                </Text>
                <View style={styles.hhCellActions}>
                  <TouchableOpacity
                    style={[styles.editButton, isBusy && styles.editButtonDisabled]}
                    onPress={() => startEditing(special)}
                    disabled={isBusy}
                    activeOpacity={0.8}
                  >
                    <Text style={styles.editButtonText}>Edit</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}

          {isAdding
            ? renderEditor({
                dayPicker: true,
                dayLabel: newDay ? DAY_ABBREV[newDay] : "—",
              })
            : null}
        </View>
      )}

      {!isBusy && availableDays.length > 0 ? (
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => startAdding(availableDays)}
          activeOpacity={0.8}
        >
          <Text style={styles.addButtonText}>Add new happy hour</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

/**
 * Loads name, address, and photos for a Google Place ID via Places API only.
 * Happy hour data comes from Convex daily_specials.
 */
export default function GooglePlaceDetailsCard({ placeId, databaseId }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setDetails(null);

      if (!placeId) {
        setLoading(false);
        setError("Missing place ID");
        return;
      }

      try {
        const result = await fetchPlaceDisplayDetails(placeId);
        if (!cancelled) {
          setDetails(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load place details");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [placeId]);

  if (loading) {
    return (
      <View style={[styles.card, styles.cardLoading]}>
        <ActivityIndicator size="small" color="#4e2916" />
        <IdRow label="Place ID" value={placeId} source={["google", "disgo"]} />
        <IdRow label="Database ID" value={databaseId} source="disgo" />
      </View>
    );
  }

  if (error || !details) {
    return (
      <View style={styles.card}>
        <View style={styles.inner}>
          <Text style={styles.errorText}>{error || "Place not found"}</Text>
          <IdRow label="Place ID" value={placeId} source={["google", "disgo"]} />
          <IdRow label="Database ID" value={databaseId} source="disgo" labelStyle={styles.idLabelSpaced} />
          <HappyHourSection databaseId={databaseId} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.inner}>
        <View style={styles.photosBlock}>
          <FieldLabel label="Photos" source="google" />
          <PlacePhotoCarousel photoUrls={details.photoUrls} height={220} />
        </View>
        <View style={styles.body}>
          <View style={styles.namedField}>
            <FieldLabel label="Name" source="google" />
            <Text style={styles.name}>{details.name}</Text>
          </View>
          {details.address ? (
            <View style={styles.namedField}>
              <FieldLabel label="Address" source="google" />
              <Text style={styles.address}>{details.address}</Text>
            </View>
          ) : null}
          <View style={styles.divider} />
          <IdRow label="Place ID" value={details.id || placeId} source={["google", "disgo"]} />
          <IdRow
            label="Database ID"
            value={databaseId}
            source="disgo"
            labelStyle={styles.idLabelSpaced}
          />
          <HappyHourSection databaseId={databaseId} />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    alignSelf: "stretch",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E6DDD6",
    overflow: "hidden",
    shadowColor: "#4e2916",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.12,
    shadowRadius: 14,
    elevation: 5,
  },
  cardLoading: {
    padding: 24,
    alignItems: "stretch",
    gap: 12,
    minHeight: 160,
    justifyContent: "center",
  },
  inner: {
    padding: 16,
    gap: 12,
  },
  body: {
    gap: 6,
  },
  photosBlock: {
    gap: 8,
  },
  namedField: {
    gap: 4,
  },
  fieldLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  name: {
    fontSize: 20,
    fontWeight: "700",
    color: "#4e2916",
    letterSpacing: -0.3,
  },
  address: {
    fontSize: 14,
    color: "#6B5A4E",
    lineHeight: 20,
  },
  divider: {
    height: 1,
    backgroundColor: "#F0E8E1",
    marginTop: 8,
    marginBottom: 4,
  },
  idBlock: {
    gap: 4,
  },
  idRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  placeIdLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: "#A89080",
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  idLabelSpaced: {
    marginTop: 8,
  },
  placeIdHint: {
    flex: 1,
    fontSize: 11,
    color: "#999",
    fontFamily: "monospace",
  },
  copyButton: {
    flexShrink: 0,
  },
  errorText: {
    fontSize: 14,
    color: "#a33",
  },
  hhSection: {
    marginTop: 12,
    gap: 8,
  },
  hhTitle: {
    fontSize: 11,
    fontWeight: "600",
    color: "#A89080",
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  hhEmpty: {
    fontSize: 13,
    color: "#999",
  },
  addButton: {
    alignSelf: "flex-start",
    marginTop: 4,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D6C4B8",
    backgroundColor: "#ffffff",
  },
  addButtonText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#4e2916",
  },
  dayPickerRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    paddingHorizontal: 10,
  },
  dayChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#D6C4B8",
    backgroundColor: "#ffffff",
  },
  dayChipActive: {
    backgroundColor: "#4e2916",
    borderColor: "#4e2916",
  },
  dayChipText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B5A4E",
  },
  dayChipTextActive: {
    color: "#ffffff",
  },
  hhTable: {
    borderRadius: 10,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#E6DDD6",
  },
  hhRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 8,
    paddingHorizontal: 10,
    gap: 8,
  },
  hhRowCentered: {
    alignItems: "center",
  },
  hhHeaderRow: {
    backgroundColor: "rgba(156, 107, 66, 0.12)",
  },
  hhRowEven: {
    backgroundColor: "#ffffff",
  },
  hhRowOdd: {
    backgroundColor: "rgba(156, 107, 66, 0.06)",
  },
  hhRowMuted: {
    opacity: 0.45,
  },
  hhHeaderText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#4e2916",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  hhCellDay: {
    width: 36,
    flexShrink: 0,
    fontSize: 13,
    color: "#4e2916",
  },
  hhDayText: {
    fontWeight: "700",
  },
  hhDayAlignInput: {
    paddingTop: 8,
  },
  hhCellTime: {
    width: 100,
    flexShrink: 0,
  },
  hhCellTimeText: {
    width: 100,
    flexShrink: 0,
    fontSize: 12,
    color: "#5C4A3E",
  },
  hhCellDesc: {
    flex: 1,
  },
  hhCellDescText: {
    flex: 1,
    fontSize: 12,
    color: "#5C4A3E",
  },
  hhCellActions: {
    width: 52,
    flexShrink: 0,
    alignItems: "flex-end",
    justifyContent: "center",
  },
  editButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D6C4B8",
    backgroundColor: "#ffffff",
  },
  editButtonDisabled: {
    opacity: 0.4,
  },
  editButtonText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#4e2916",
  },
  hhEditBlock: {
    paddingBottom: 10,
    gap: 8,
  },
  hhEditActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 10,
  },
  cancelButton: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "#D6C4B8",
    backgroundColor: "#ffffff",
  },
  cancelButtonText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#6B5A4E",
  },
  saveButton: {
    backgroundColor: "#4e2916",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    minWidth: 72,
    alignItems: "center",
    justifyContent: "center",
  },
  saveButtonDisabled: {
    opacity: 0.4,
  },
  saveButtonText: {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: "600",
  },
  saveError: {
    fontSize: 13,
    color: "#a33",
    paddingHorizontal: 10,
  },
  hhInput: {
    fontSize: 12,
    color: "#5C4A3E",
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#E6DDD6",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    minHeight: 34,
  },
});
